import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Home,
  Users,
  Map,
  Wallet,
  Library,
  Sparkles,
  User,
  LogOut,
  Menu,
  X,
  LayoutDashboard,
  ChevronRight,
  Swords,
  ShieldCheck,
} from "lucide-react";
import logoSvg from "@/assets/logo.svg";
import { useAuth } from "@/hooks/useAuth";
import { useHousehold } from "@/hooks/useHousehold";
import { useWallet } from "@/hooks/useWallet";
import { useProfile } from "@/hooks/useProfile";
import { useProfileNotifications } from "@/hooks/useProfileNotifications";
import NotificationsBell from "@/components/notifications/NotificationsBell";
import { cn } from "@/lib/utils";
import { useAccessTier } from "@/hooks/useAccessTier";
import { useHouseholdRole } from "@/hooks/useHouseholdRole";
import { isKnownParentEmail } from "@/lib/qaAccounts";

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  match?: (path: string) => boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Home", icon: Home, to: "/dashboard" },
  { label: "Instructors", icon: Sparkles, to: "/guides" },
  { label: "Roadmap", icon: Map, to: "/curriculum" },
  { label: "Finance Center", icon: Wallet, to: "/finances" },
  { label: "Resource Hub", icon: Library, to: "/resources" },
  { label: "Community", icon: Users, to: "/friends" },
  { label: "Challenge Center", icon: Swords, to: "/challenges" },
];

const PARENT_NAV_ITEMS: NavItem[] = [
  { label: "Parent Hub", icon: LayoutDashboard, to: "/parent-dashboard" },
  { label: "Parent Privacy", icon: ShieldCheck, to: "/parent/privacy" },
  { label: "Support", icon: Library, to: "/support" },
];

export default function DashboardSidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { isParent } = useHousehold();
  const { isParent: roleParent } = useHouseholdRole();
  const { balance, loading: walletLoading } = useWallet();
  const { profile } = useProfile();
  const { totalCount: profileBadgeCount } = useProfileNotifications();
  const { tier } = useAccessTier();
  const beta = false; // paid product: no restricted tier
  const [mobileOpen, setMobileOpen] = useState(false);
  const parentMode = isKnownParentEmail(user?.email) || isParent || roleParent || pathname.startsWith("/parent-dashboard") || pathname.startsWith("/parent/");
  // Parents get BOTH the parent navigation AND the full learner navigation
  // so they can experience the beta path and platform alongside their child.
  const learnerItems = beta ? NAV_ITEMS.filter((item) => item.to !== "/curriculum") : NAV_ITEMS;
  // Learner experience comes first so parents land in the platform they're guiding,
  // with parent-only tools grouped at the bottom of the sidebar.
  const navItems = parentMode ? [...learnerItems, ...PARENT_NAV_ITEMS] : learnerItems;
  // Only hide the wallet card while actively viewing parent-only routes.
  const onParentHub = pathname.startsWith("/parent-dashboard") || pathname.startsWith("/parent/");

  const displayName =
    (profile)?.display_name?.split(" ")[0] ||
    user?.email?.split("@")[0] ||
    (parentMode ? "Parent" : "Learner");
  const initial = displayName.charAt(0).toUpperCase();

  const isActive = (to: string) => {
    const base = to.split("?")[0];
    return pathname === base || (base === "/dashboard" && pathname === "/");
  };

  const Sidebar = (
    <aside className="h-full w-64 shrink-0 flex flex-col bg-card/60 backdrop-blur-xl border-r border-border/50">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-5 py-5">
        <div className="relative">
          <div className="absolute inset-0 rounded-xl bg-primary/30 blur-md" />
          <img src={logoSvg} alt="HeirWise" className="relative h-8 w-8 rounded-lg" />
        </div>
        <span className="font-heading font-bold text-base text-foreground">HeirWise</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const active = isActive(item.to);
          const Icon = item.icon;
          return (
            <NavLink
              key={item.label}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={cn(
                "group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all relative overflow-hidden",
                active
                  ? "bg-primary/20 text-foreground shadow-[0_0_20px_hsl(var(--primary)/0.25)] border border-primary/40"
                  : "text-foreground/70 hover:text-foreground hover:bg-muted/50"
              )}
            >
              {active && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-gradient-to-b from-primary to-secondary" />
              )}
              <Icon className={cn("w-4 h-4 shrink-0", active && "text-primary")} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}

        {!parentMode && isParent && (
          <NavLink
            to="/parent-dashboard"
            onClick={() => setMobileOpen(false)}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
              pathname === "/parent-dashboard"
                ? "bg-primary/15 text-foreground border border-primary/30"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
            )}
          >
            <LayoutDashboard className="w-4 h-4 shrink-0" />
            <span>Parent Hub</span>
          </NavLink>
        )}
      </nav>

      {/* Bottom callout */}
      <div className="p-3 space-y-2 border-t border-border/40">
        {/* Wallet quick card → Finance Center (hidden on Parent Hub) */}
        {!onParentHub && (
        <button
          onClick={() => {
            setMobileOpen(false);
            navigate("/finances");
          }}
          className="w-full text-left rounded-xl border border-primary/30 bg-gradient-to-br from-primary/15 via-secondary/10 to-transparent p-3 relative overflow-hidden hover:border-primary/50 hover:shadow-[0_0_20px_hsl(var(--primary)/0.25)] transition-all group"
        >
          <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-secondary/20 blur-2xl" />
          <div className="relative flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0">
              <Wallet className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-none">
                Wallet
              </p>
              <p className="font-heading font-bold text-base text-foreground leading-tight mt-0.5">
                {walletLoading ? "—" : `$${balance}`}
              </p>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5 relative leading-snug">
            {walletLoading ? "Loading balance…" : balance > 0 ? "Simulated balance" : "Start lessons to earn"}
          </p>
        </button>
        )}

        {/* Profile + Notifications + Sign out */}
        <div className="flex items-center gap-1.5 rounded-xl bg-muted/30 border border-border/40 p-1.5">
          <button
            onClick={() => {
              setMobileOpen(false);
              navigate("/profile");
            }}
            className="flex-1 flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-muted/50 transition-colors min-w-0"
            title="Profile & Settings"
          >
            <div className="relative h-7 w-7 rounded-full bg-gradient-to-br from-primary to-secondary text-primary-foreground font-heading font-bold text-xs flex items-center justify-center border border-primary/40 shrink-0">
              {initial}
              {profileBadgeCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center">
                  {profileBadgeCount > 9 ? "9+" : profileBadgeCount}
                </span>
              )}
            </div>
            <span className="text-xs font-medium text-foreground truncate">{displayName}</span>
          </button>
          <NotificationsBell />
          <button
            onClick={signOut}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            title="Sign out"
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <>
      {/* Mobile toggle button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-3 left-3 z-40 p-2 rounded-xl bg-card/80 backdrop-blur-xl border border-border/50 text-foreground shadow-lg lg:hidden"
        aria-label="Open menu"
      >
        <Menu className="w-4 h-4" />
      </button>

      {/* Desktop sticky */}
      <div className="hidden lg:flex sticky top-0 h-screen">{Sidebar}</div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative animate-in slide-in-from-left duration-200">
            {Sidebar}
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-3 p-2 rounded-lg text-muted-foreground hover:text-foreground"
              aria-label="Close menu"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}